import { useState } from "react";
import {
  DATE_OPTIONS,
  DURATION_OPTIONS,
  VIDEO_TYPES,
  type FilterState,
} from "@/utils/video";

interface FilterBarProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  resultCount: number;
}

function OptionButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
        active
          ? "border-primary-500 bg-primary-600/20 text-primary-200"
          : "border-background-700 bg-background-800/60 text-foreground-400 hover:border-background-600 hover:text-foreground-200"
      }`}
    >
      {label}
    </button>
  );
}

export default function FilterBar({ filters, onChange, onReset, resultCount }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const isDirty =
    filters.type !== "all" || filters.duration !== "all" || filters.date !== "all";

  return (
    <div className="rounded-lg border border-background-800 bg-background-900">
      <div className="flex items-center justify-between gap-3 border-b border-background-800 px-4 py-3 lg:border-b-0">
        <div className="flex items-center gap-2 text-sm text-foreground-300">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-filter-3-line text-sm text-primary-300" />
          </span>
          筛选条件
          <span className="text-xs text-foreground-600">共 {resultCount} 部</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-700 px-3 py-1.5 text-xs text-foreground-300 lg:hidden"
            aria-expanded={open}
          >
            {open ? "收起" : "展开"}
            <span className="flex h-3 w-3 items-center justify-center">
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? "rotate-180" : ""}`} />
            </span>
          </button>
          {isDirty && (
            <button
              type="button"
              onClick={onReset}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-primary-300 transition-colors hover:text-primary-200"
            >
              <i className="ri-refresh-line text-xs" />
              重置
            </button>
          )}
        </div>
      </div>

      <div className={`${open ? "flex" : "hidden"} flex-col gap-4 px-4 py-4 lg:flex lg:flex-row lg:items-start lg:gap-8 lg:py-5`}>
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="w-16 shrink-0 text-xs text-foreground-600">视频类型</span>
            <div className="flex flex-wrap gap-2">
              <OptionButton
                active={filters.type === "all"}
                label="全部"
                onClick={() => onChange({ ...filters, type: "all" })}
              />
              {VIDEO_TYPES.map((type) => (
                <OptionButton
                  key={type}
                  active={filters.type === type}
                  label={type}
                  onClick={() => onChange({ ...filters, type })}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="w-16 shrink-0 text-xs text-foreground-600">视频时长</span>
            <div className="flex flex-wrap gap-2">
              {DURATION_OPTIONS.map((option) => (
                <OptionButton
                  key={option.value}
                  active={filters.duration === option.value}
                  label={option.label}
                  onClick={() => onChange({ ...filters, duration: option.value })}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="w-16 shrink-0 text-xs text-foreground-600">发布日期</span>
            <div className="flex flex-wrap gap-2">
              {DATE_OPTIONS.map((option) => (
                <OptionButton
                  key={option.value}
                  active={filters.date === option.value}
                  label={option.label}
                  onClick={() => onChange({ ...filters, date: option.value })}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 border-t border-background-800 pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
          <span className="text-xs text-foreground-600">排序</span>
          <OptionButton
            active={filters.sort === "latest"}
            label="最新发布"
            onClick={() => onChange({ ...filters, sort: "latest" })}
          />
          <OptionButton
            active={filters.sort === "popular"}
            label="最多播放"
            onClick={() => onChange({ ...filters, sort: "popular" })}
          />
        </div>
      </div>
    </div>
  );
}